import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Loader2, Mail, Lock, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

const GoogleIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18A11 11 0 0 0 1 12c0 1.77.43 3.45 1.18 4.94l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
  </svg>
);

const AuthModal = ({ open, onOpenChange }) => {
  const { loginWithPassword, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('signin'); // signin | signup
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      if (tab === 'signup') {
        await register(name, email, password);
      } else {
        await loginWithPassword(email, password);
      }
      onOpenChange(false);
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const switchTab = (t) => { setTab(t); setError(''); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-2xl" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-[#1A1514]">
            {tab === 'signin' ? 'Welcome back' : 'Create your free account'}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#8C7A76]">
            {tab === 'signin' ? 'Sign in to your InnerGlow account.' : 'Track your skin, get AI insights — free.'}
          </DialogDescription>
        </DialogHeader>

        <Button type="button" onClick={loginWithGoogle}
          className="w-full rounded-full h-11 font-semibold bg-[#FF6B4A] hover:bg-[#E8552F] text-white"
          data-testid="auth-google-btn">
          <span className="bg-white rounded-full p-1 inline-flex items-center justify-center"><GoogleIcon /></span>
          <span className="ml-2">Continue with Google</span>
        </Button>

        <div className="flex items-center gap-3 my-1">
          <span className="flex-1 h-px bg-[#F3E6E0]" />
          <span className="text-xs text-[#8C7A76]">or use email</span>
          <span className="flex-1 h-px bg-[#F3E6E0]" />
        </div>

        <div className="flex rounded-full bg-[#F3E6E0] p-1 mb-1">
          <button onClick={() => switchTab('signin')}
            className={`flex-1 text-sm font-semibold py-2 rounded-full transition-colors ${tab === 'signin' ? 'bg-[#FF6B4A] text-white' : 'text-[#FF6B4A]'}`}
            data-testid="auth-tab-signin">Sign in</button>
          <button onClick={() => switchTab('signup')}
            className={`flex-1 text-sm font-semibold py-2 rounded-full transition-colors ${tab === 'signup' ? 'bg-[#FF6B4A] text-white' : 'text-[#FF6B4A]'}`}
            data-testid="auth-tab-signup">Sign up</button>
        </div>

        <form onSubmit={submit} className="space-y-3">
          {tab === 'signup' && (
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8C7A76]" />
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" required
                className="pl-9 h-11 rounded-xl" data-testid="auth-name-input" />
            </div>
          )}
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8C7A76]" />
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address" required
              className="pl-9 h-11 rounded-xl" data-testid="auth-email-input" />
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8C7A76]" />
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder={tab === 'signup' ? 'Password (min 8 characters)' : 'Password'} required minLength={8}
              className="pl-9 h-11 rounded-xl" data-testid="auth-password-input" />
          </div>

          {error && (
            <p className="text-sm text-[#FF3B30] bg-[#FF3B30]/8 rounded-lg px-3 py-2" data-testid="auth-error">{error}</p>
          )}

          <Button type="submit" disabled={busy} variant="outline"
            className="w-full rounded-full h-11 font-semibold border-[#FF6B4A] text-[#FF6B4A] hover:bg-[#FF6B4A]/5"
            data-testid="auth-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : tab === 'signin' ? 'Sign in' : 'Create account'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
